import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  User, Building2, Mail, Phone, Globe, CreditCard, MapPin, FileCheck, Calendar, Shield, Download,
} from "lucide-react";

interface AffiliateApplication {
  applicant_name: string;
  applicant_type: string;
  company_name: string | null;
  email: string;
  phone: string | null;
  website_url: string | null;
  experience: string | null;
  commission_preference: string;
  approval_status: string;
  created_at: string;
  bank_info: any;
  national_address: any;
  person_in_charge_name: string | null;
  person_in_charge_title: string | null;
  cr_number: string | null;
  vat_number: string | null;
  document_urls: any;
  national_id_number: string | null;
  date_of_birth: string | null;
  agreement_accepted_at?: string | null;
  agreement_signature_url?: string | null;
  agreement_stamp_url?: string | null;
  agreement_pdf_url?: string | null;
  country?: string | null;
}

interface AffiliateProfileTabProps {
  application: AffiliateApplication | null;
}

export const AffiliateProfileTab = ({ application }: AffiliateProfileTabProps) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const isRTL = language === 'ar';

  const handleDownloadAgreement = async () => {
    if (!application?.agreement_pdf_url) return;
    try {
      const { data, error } = await supabase.storage
        .from('affiliate-documents')
        .createSignedUrl(application.agreement_pdf_url, 600);
      if (error) throw error;
      window.open(data.signedUrl, '_blank');
    } catch (e) {
      console.error(e);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "تعذّر تحميل الاتفاقية" : "Could not download the agreement",
        variant: "destructive",
      });
    }
  };

  if (!application) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <User className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p>{isRTL ? "لم يتم العثور على ملف تعريف" : "Profile not found"}</p>
        </CardContent>
      </Card>
    );
  }

  const isAgency = application.applicant_type === 'agency';
  const bankInfo = application.bank_info || {};
  const nationalAddress = application.national_address || {};
  const docs = application.document_urls || {};

  const maskIban = (iban: string) => {
    if (!iban || iban.length < 8) return iban || '-';
    return iban.substring(0, 4) + '****' + iban.substring(iban.length - 4);
  };

  const docCount = Object.keys(docs).filter(k => docs[k]).length;

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              {isAgency ? <Building2 className="h-8 w-8 text-primary" /> : <User className="h-8 w-8 text-primary" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold">{application.applicant_name}</h2>
                <Badge variant={application.approval_status === 'approved' ? 'default' : 'secondary'}>
                  {application.approval_status === 'approved' ? (isRTL ? "معتمد" : "Verified") : application.approval_status}
                </Badge>
                <Badge variant="outline">
                  {isAgency ? (isRTL ? "وكالة" : "Agency") : (isRTL ? "فرد" : "Individual")}
                </Badge>
              </div>
              {application.company_name && (
                <p className="text-muted-foreground mt-1">{application.company_name}</p>
              )}
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {application.email}</span>
                {application.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {application.phone}</span>}
                {application.website_url && <span className="flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> {application.website_url}</span>}
                <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {isRTL ? "انضم" : "Joined"} {new Date(application.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* KYC / Documents */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileCheck className="h-4 w-4" />
              {isRTL ? "حالة التحقق" : "Verification Status"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isAgency ? (
              <>
                <InfoRow label={isRTL ? "رقم السجل التجاري" : "CR Number"} value={application.cr_number} />
                <InfoRow label={isRTL ? "الرقم الضريبي" : "VAT Number"} value={application.vat_number} />
                <InfoRow label={isRTL ? "المسؤول" : "Person in Charge"} value={application.person_in_charge_name} />
                <InfoRow label={isRTL ? "المسمى الوظيفي" : "Title"} value={application.person_in_charge_title} />
              </>
            ) : (
              <>
                <InfoRow label={application.country === "SA" ? (isRTL ? "الهوية الوطنية أو الإقامة" : "National ID or Iqama") : (isRTL ? "رقم الهوية" : "National ID")} value={application.national_id_number ? '****' + application.national_id_number.slice(-4) : null} />
                <InfoRow label={isRTL ? "تاريخ الميلاد" : "Date of Birth"} value={application.date_of_birth} />
              </>
            )}
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{isRTL ? "المستندات المرفوعة" : "Documents Uploaded"}</span>
              <Badge variant={docCount > 0 ? "default" : "outline"}>
                <Shield className="h-3 w-3 mr-1" />
                {docCount} {isRTL ? "مستند" : "docs"}
              </Badge>
            </div>
            {application.agreement_accepted_at && (
              <>
                <Separator />
                <InfoRow
                  label={isRTL ? "تم قبول الاتفاقية" : "Agreement Accepted"}
                  value={new Date(application.agreement_accepted_at).toLocaleString()}
                />
                {application.agreement_pdf_url && (
                  <Button
                    onClick={handleDownloadAgreement}
                    size="sm"
                    className="w-full"
                  >
                    <Download className="h-3.5 w-3.5 mr-2" />
                    {isRTL ? "تحميل الاتفاقية الموقعة (PDF)" : "Download Signed Agreement (PDF)"}
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Bank Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              {isRTL ? "معلومات البنك" : "Bank Information"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow label={isRTL ? "اسم البنك" : "Bank Name"} value={bankInfo.bankName} />
            <InfoRow label={isRTL ? "IBAN" : "IBAN"} value={maskIban(bankInfo.iban)} />
            <InfoRow label={isRTL ? "اسم صاحب الحساب" : "Account Holder"} value={bankInfo.accountHolder} />
            <Separator />
            <InfoRow label={isRTL ? "تفضيل العمولة" : "Commission Preference"} value={application.commission_preference} />
          </CardContent>
        </Card>

        {/* National Address */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {isRTL ? "العنوان الوطني" : "National Address"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow label={isRTL ? "رقم المبنى" : "Building No."} value={nationalAddress.buildingNumber} />
            <InfoRow label={isRTL ? "الشارع" : "Street"} value={nationalAddress.streetName} />
            <InfoRow label={isRTL ? "الحي" : "District"} value={nationalAddress.district} />
            <InfoRow label={isRTL ? "المدينة" : "City"} value={nationalAddress.city} />
            <InfoRow label={isRTL ? "الرمز البريدي" : "Postal Code"} value={nationalAddress.postalCode} />
          </CardContent>
        </Card>

        {/* Experience */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{isRTL ? "الخبرة" : "Experience"}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{application.experience || (isRTL ? "لم يتم تحديد" : "Not specified")}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value || '-'}</span>
  </div>
);
